import { useTinyHost } from "@tiny/host-contract";
import React from "react";

import { RowAction } from "@tiny/components/src/ListItemRow/ListItemRow.types";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import { useAudienceListView } from "../AudienceList.context";
import {
  getTimeActionsInfo,
  toChipColor,
  toChipText,
} from "./AudienceRow.helper";
import { AudienceRowProps } from "./AudienceRow.types";
import useTinyImporter from "../../../hooks/useTinyImporter";
import { showUploadMessage } from "@tiny/components/src/Snackbar/snackbar-helpers";
import { closeSnackbar } from "notistack";

export function useAudienceRow(props: AudienceRowProps) {
  const { tinyImporterOpen, closeTinyImporter, openTinyImporter } =
    useTinyImporter();

  const { audience, seeAllSubcribersOnUpload = false } = props;

  const { mdUp } = useBreakpoints();
  const { capabilities } = useTinyHost();
  const { isShopifyPlan } = capabilities;
  // Default (undefined) = visible; a host can set false to hide (TinyAlbert).
  const cleanListVisible = capabilities.cleanListVisible !== false;

  const { isAudienceDeletable, selectionDisabled } = useAudienceListView();

  const {
    enableBulkDelete,
    toggleSelectItem,
    isItemSelected,
    sortOption,
    onDeleteAudience,
    reloadAudiences,
  } = useAudienceListView();

  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [mutationLoading, setMutationLoading] = React.useState(false);

  const [cleanedHistoryDialogOpen, setCleanedHistoryDialogOpen] =
    React.useState(false);

  const [productDetailsDialogOpen, setProductDetailsDialogOpen] =
    React.useState(false);

  const moreButtonRef = React.useRef<HTMLButtonElement | null>(null);

  const showMore = React.useCallback(() => setMoreOpen(true), []);
  const hideMore = React.useCallback(() => setMoreOpen(false), []);
  const toggleDetails = React.useCallback(
    () => setDetailsOpen((prev) => !prev),
    []
  );

  const deletable = React.useMemo(
    () => isAudienceDeletable(audience),
    [audience, isAudienceDeletable]
  );

  const onSelect = React.useCallback(
    () => toggleSelectItem(audience),
    [audience, toggleSelectItem]
  );

  const isSelected = React.useMemo(
    () => () => isItemSelected(audience),
    [audience, isItemSelected]
  );

  const disabledCheckbox = React.useMemo(
    () => selectionDisabled,
    [selectionDisabled]
  );

  const disabledCheckboxToolTip = React.useMemo(() => {
    const baseCondition = disabledCheckbox && enableBulkDelete;
    if (baseCondition && audience.segmentType === "INTEGRATION") {
      return "Cannot delete customers via integrations. Please contact support for help.";
    } else if (baseCondition && audience.assignedTo?.length) {
      return "Audience can not be deleted because it's assigned to active Campaign, Form";
    }
    return undefined;
  }, [
    audience.assignedTo?.length,
    audience.segmentType,
    disabledCheckbox,
    enableBulkDelete,
  ]);

  const timeActions = React.useMemo(
    () => getTimeActionsInfo(mdUp, audience, sortOption.action),
    [audience, mdUp, sortOption.action]
  );

  const chipColor = React.useMemo(
    () => toChipColor(audience.segmentType),
    [audience.segmentType]
  );

  const chipText = React.useMemo(
    () => toChipText(audience.segmentType),
    [audience.segmentType]
  );

  const subscribersText = React.useMemo(
    () => `Customer${audience.numberOfCustomers === 1 ? "" : "s"}`,
    [audience.numberOfCustomers]
  );

  const moreActions = React.useMemo(() => {
    const addToExistingAction = {
      name: "Add Customers",
      onClickAction: openTinyImporter,
      isFlatFileAction: true,
    };

    const deleteAction = {
      name: "Delete",
      onClickAction: async () => {
        showUploadMessage({
          variant: "success",
          message: "Deletion started!",
          description: `Your [${audience.name}] delete is now in progress. Please refresh to see the current status`,
          primaryCtaActionText: "Close",
          primaryCtaActionFunction: () => {
            closeSnackbar();
          },
          persist: true,
        });
        onDeleteAudience(audience);
        reloadAudiences();
      },
    };

    const cleanListAction = {
      name: "Clean My List",
      onClickAction: () => setProductDetailsDialogOpen(true),
    };

    const downloadCleanedListAction = {
      name: "Download Cleaned List",
      onClickAction: () => setCleanedHistoryDialogOpen(true),
    };

    let rowActions: RowAction[] =
      mdUp &&
      (audience.status === "COMPLETED" || audience.status === "VERIFICATION")
        ? [addToExistingAction]
        : [];

    if (audience.segmentType !== "UPLOAD") {
      rowActions = [];
    }

    if (
      cleanListVisible &&
      !isShopifyPlan &&
      audience.segmentType === "UPLOAD" &&
      (audience.cleaned || audience.status === "COMPLETED")
    ) {
      rowActions.push(cleanListAction);
    }

    if (
      cleanListVisible &&
      !isShopifyPlan &&
      audience.segmentType === "UPLOAD" &&
      audience.cleaned &&
      audience?.status === "COMPLETED"
    ) {
      rowActions.push(downloadCleanedListAction);
    }

    if (deletable) {
      rowActions.push(deleteAction);
    }

    return rowActions;
  }, [
    audience,
    deletable,
    mdUp,
    onDeleteAudience,
    isShopifyPlan,
    cleanListVisible,
  ]);

  const checked = isSelected ? isSelected() : false;

  const audienceLocation = {
    pathname: `/main/audience/sources/${audience.id}`,
    state: {
      backUrl: { pathname: location.pathname, search: location.search },
    },
  };

  const statusMap = {
    COMPLETED: "Completed",
    FAILED: "Failed",
    IN_PROGRESS: "Processing",
    DELETING: "Deleting",
    VERIFICATION: "Completed",
  };

  return {
    onSelect,
    subscribersText,
    disabledCheckbox,
    disabledCheckboxToolTip,
    sortAction: sortOption.action,
    timeActions,
    chipColor,
    chipText,
    moreActions,
    detailsOpen,
    moreButtonRef,
    checked,
    moreOpen,
    mutationLoading,
    toggleDetails,
    showMore,
    hideMore,
    selectionDisabled,
    audienceLocation,
    tinyImporterOpen,
    openTinyImporter,
    closeTinyImporter,
    statusMap,
    cleanedHistoryDialogOpen,
    openCleanedHistoryDialog: () => setCleanedHistoryDialogOpen(true),
    closeCleanedHistoryDialog: () => setCleanedHistoryDialogOpen(false),
    productDetailsDialogOpen,
    openProductDetailsDialog: () => setProductDetailsDialogOpen(true),
    closeProductDetailsDialog: () => setProductDetailsDialogOpen(false),
  };
}
